/**
 * Custom hook for managing chat state and API interactions.
 *
 * Handles message sending, history loading, and local state management
 * for the AI study assistant chat widget.
 */

import { chatApi } from "@api/chatApi";
import type {
  ChatHistoryMessage,
  ChatMessageRequest
} from "@/ts/interfaces/chat/chatInterface";
import { useCallback, useEffect, useRef, useState } from "react";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface UseChatOptions {
  lessonKey: string;
}

interface UseChatReturn {
  messages: ChatMessage[];
  isLoading: boolean;
  isSending: boolean;
  error: string | null;
  sendMessage: (message: string) => Promise<void>;
  clearHistory: () => Promise<void>;
  loadHistory: () => Promise<void>;
}

const mapHistoryMessage = (msg: ChatHistoryMessage): ChatMessage => ({
  id: msg.id,
  role: msg.role,
  content: msg.content,
  timestamp: new Date(msg.created_at)
});

export function useChat({ lessonKey }: UseChatOptions): UseChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const historyLoadedRef = useRef(false);

  const loadHistory = useCallback(async () => {
    if (!lessonKey) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await chatApi.getChatHistory(lessonKey);
      if (response.messages && response.messages.length > 0) {
        setMessages(response.messages.map(mapHistoryMessage));
      }
    } catch (err) {
      console.error("[useChat] Failed to load chat history:", err);
      // Don't show error for history load failure - just start fresh
    } finally {
      setIsLoading(false);
    }
  }, [lessonKey]);

  // Load history once when lessonKey is available
  useEffect(() => {
    if (lessonKey && !historyLoadedRef.current) {
      historyLoadedRef.current = true;
      loadHistory();
    }
  }, [lessonKey, loadHistory]);

  const sendMessage = useCallback(
    async (message: string) => {
      if (!lessonKey || !message.trim()) return;

      setError(null);

      // Optimistically add the user message
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: message.trim(),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, userMessage]);
      setIsSending(true);

      try {
        const payload: ChatMessageRequest = {
          lesson_id: lessonKey,
          message: message.trim()
        };

        const response = await chatApi.sendChatMessage(payload);

        // Add the assistant's response
        const assistantMessage: ChatMessage = {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          content: response.response,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);
      } catch (err) {
        console.error("[useChat] Failed to send message:", err);
        setError(
          "Sorry, I couldn't get a response right now. Please try again."
        );
      } finally {
        setIsSending(false);
      }
    },
    [lessonKey]
  );

  const clearHistory = useCallback(async () => {
    if (!lessonKey) return;

    try {
      await chatApi.clearChatHistory(lessonKey);
      setMessages([]);
      setError(null);
    } catch (err) {
      console.error("[useChat] Failed to clear chat history:", err);
    }
  }, [lessonKey]);

  return {
    messages,
    isLoading,
    isSending,
    error,
    sendMessage,
    clearHistory,
    loadHistory
  };
}
